import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';
import numeral from 'numeral';

import { scrollToBlock } from '../../../helpers/scrollToBlock';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import EvidenceBlock from '../GscTools/components/EvidenceBlock';
import ScopeControls from '../GscTools/components/ScopeControls';
import { downloadCsv } from '../GscTools/csv';
import { hasDuplicateKeys, reportsPosition } from '../GscTools/metrics';
import { DETAIL_MAX_DAYS, defaultPeriod, formatPeriod, periodEndingOn } from '../GscTools/period';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { readParams, writeParams } from '../GscTools/url';

import ReviewFilter from './components/ReviewFilter';
import RuleControls from './components/RuleControls';
import Shortlist from './components/Shortlist';
import { buildReviewCsv } from './exportRows';
import {
    candidateColumns,
    evidenceActionColumn,
    evidenceColumns,
    evidenceLinkColumn,
    fieldValueColumns,
} from './keys';
import { fetchQueryPages, fetchShortlistChunk } from './query';
import {
    candidateRows,
    coverageCounts,
    filterByReview,
    readSelection,
    shortlistRows,
} from './rows';
import { EVALUATION_CONCURRENCY, MAX_SELECTED, REVIEW, STATE, ruleFromParams } from './rule';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
    reviewFromParams,
} from './scope';

const IDLE_SHORTLIST = { status: 'idle', error: null, chunk: null, loadingMore: false };

/**
 * Cannibalization Review — selected queries for which Search Console returned more than one of the
 * property's URLs, laid out for a person to review.
 *
 * It is a review aid and says so throughout. Several URLs shown for one query is a fact about what
 * Search Console returned; it does not establish that the pages competed, that either lost
 * traffic, or that merging them would help — navigational queries, sitelinks and genuinely
 * different intents all produce the same pattern. Nothing here scores severity, estimates lost
 * clicks or suggests a redirect, a canonical or a merge.
 *
 * The work is bounded and explicit. Applying a scope retrieves one shortlist of queries. Nothing
 * further is asked until the user selects queries — at most twenty — and presses Evaluate, which
 * sends one page request per selected query not already evaluated: the query as an exact `equals`
 * filter, the page as the dimension. The requests run a few at a time and land independently, so
 * a failure leaves the others standing and its query marked as not evaluated rather than as clean.
 *
 * The candidate rule is read in the browser over the evaluations already held. Changing it costs
 * nothing and every block follows at once; it never reaches Search Console. The share denominator
 * is every page row retrieved for the exact query, including the URLs below the share threshold.
 *
 * A scope generation is bumped by every apply and refresh, so a slow shortlist or evaluation for a
 * scope the user has moved on from is dropped when it lands. Nothing is persisted: the scope, the
 * rule and the review view live in the page's address; the selection deliberately does not.
 */
class Cannibalization extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleToggleQuery = this.handleToggleQuery.bind(this);
        this.handleSelectTop = this.handleSelectTop.bind(this);
        this.handleClearSelection = this.handleClearSelection.bind(this);
        this.handleEvaluate = this.handleEvaluate.bind(this);
        this.handleRule = this.handleRule.bind(this);
        this.handleReview = this.handleReview.bind(this);
        this.handleOpenEvidence = this.handleOpenEvidence.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();
        this.evidenceRef = createRef();

        this.generation = 0;
        this.isMounted_ = false;

        const period = defaultPeriod(DETAIL_MAX_DAYS);

        // The address is read once, here, so the controls show the report it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            latestDate: '',

            draft: { ...initialDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            shortlist: { ...IDLE_SHORTLIST },
            // The user's selection, in the order it was made, and what each evaluation returned.
            selected: [],
            evaluations: {},
            evidenceQuery: null,

            rule: ruleFromParams(params),
            review: reviewFromParams(params),
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                // Kept as well as applied: the date presets count back from this day, so one
                // click lands on days the property has data for.
                if (!seedDates) return { latestDate: latest };

                return {
                    latestDate: latest,
                    draft: { ...state.draft, ...periodEndingOn(latest, DETAIL_MAX_DAYS) },
                };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    /** A new scope starts a new review: its selection and evaluations belonged to the old one. */
    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.load(scope, { keepSelection: false });
    }

    /**
     * A refresh repeats the same scope, so the selection the user made under it stands — and the
     * queries already evaluated are evaluated again, because their evidence is what was refreshed.
     */
    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied, { keepSelection: true });
    }

    /** The address follows the applied scope, the rule and the review view. */
    writeAddress(scope = this.state.applied, rule = this.state.rule, review = this.state.review) {
        if (scope) writeParams(reportParamsFromScope(scope, rule, review), URL_KEYS);
    }

    load(scope, { keepSelection }) {
        this.generation += 1;
        const generation = this.generation;

        this.writeAddress(scope);

        const selected = keepSelection ? this.state.selected : [];
        const reevaluate = keepSelection
            ? selected.filter((query) => !!this.state.evaluations[query])
            : [];

        this.setState({
            applied: scope,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),
            shortlist: { ...IDLE_SHORTLIST, status: 'loading' },
            selected,
            evaluations: {},
            evidenceQuery: keepSelection ? this.state.evidenceQuery : null,
        });

        this.loadShortlist(scope, generation, { more: false });
        if (reevaluate.length) this.evaluate(scope, reevaluate, generation);
    }

    /**
     * Block 01 — one chunk of the query shortlist, appended to the queries already held when
     * loading more. A query returned twice stops retrieval rather than being listed twice.
     */
    async loadShortlist(scope, generation, { more }) {
        const previous = more ? this.state.shortlist.chunk : null;
        if (more && (previous?.nextOffset ?? null) === null) return;

        this.setState((state) => ({
            shortlist: { ...state.shortlist, loadingMore: more, error: null },
        }));

        try {
            const result = await fetchShortlistChunk({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                offset: more ? previous.nextOffset : 0,
            });

            const rows = [...(previous?.rows ?? []), ...result.rows];
            if (hasDuplicateKeys(rows)) throw new Error(this.props.t('tools.gsc.duplicate_rows'));

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                loading: false,
                shortlist: {
                    status: 'done',
                    error: null,
                    loadingMore: false,
                    chunk: { ...result, rows },
                },
            });
        } catch (err) {
            console.log('shortlist', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState((state) => ({
                loading: false,
                shortlist: {
                    ...state.shortlist,
                    // A failure while loading more keeps the queries already retrieved; a failure
                    // on the first chunk has nothing to keep.
                    status: state.shortlist.chunk ? 'done' : 'error',
                    loadingMore: false,
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            }));
        }
    }

    handleLoadMore() {
        const scope = this.state.applied;
        if (scope) this.loadShortlist(scope, this.generation, { more: true });
    }

    /** The shortlist as the table shows it, rebuilt only when a chunk lands. */
    shortlistRowsFor(scope) {
        const chunk = this.state.shortlist.chunk;
        const cache = this.shortlistCache;
        if (cache && cache.chunk === chunk && cache.scope === scope) return cache.rows;

        const rows = shortlistRows(scope, chunk?.rows);
        this.shortlistCache = { chunk, scope, rows };
        return rows;
    }

    /**
     * Selecting a query costs nothing; only Evaluate sends requests. A query that is deselected
     * keeps its evaluation in memory, so selecting it again does not ask for it twice.
     */
    handleToggleQuery(query) {
        this.setState((state) => {
            if (state.selected.includes(query)) {
                return { selected: state.selected.filter((item) => item !== query) };
            }
            if (state.selected.length >= MAX_SELECTED) return null;
            return { selected: [...state.selected, query] };
        });
    }

    /** The cap's worth of queries with the most impressions, as one explicit choice. */
    handleSelectTop() {
        const scope = this.state.applied;
        if (!scope) return;

        const top = this.shortlistRowsFor(scope)
            .slice(0, MAX_SELECTED)
            .map((row) => row.key);
        this.setState({ selected: top });
    }

    handleClearSelection() {
        this.setState({ selected: [], evidenceQuery: null });
    }

    /** The selected queries Evaluate would send: those never evaluated, or that failed. */
    toEvaluate() {
        const { selected, evaluations } = this.state;
        return selected.filter(
            (query) => !evaluations[query] || evaluations[query].status === 'error',
        );
    }

    handleEvaluate() {
        const scope = this.state.applied;
        const queries = this.toEvaluate();
        if (scope && queries.length) this.evaluate(scope, queries, this.generation);
    }

    /** Records one query's evaluation without disturbing the others'. */
    setEvaluation(query, value) {
        this.setState((state) => ({ evaluations: { ...state.evaluations, [query]: value } }));
    }

    /**
     * Blocks 02 and 03 — one page request per query, a few in flight at a time. Each lands on its
     * own; a scope that has moved on stops the queue and drops whatever is still in flight.
     */
    async evaluate(scope, queries, generation) {
        for (const query of queries) {
            this.setEvaluation(query, { status: 'loading', error: null, rows: [], capped: false });
        }

        const queue = [...queries];
        const worker = async () => {
            while (queue.length) {
                if (!this.isMounted_ || generation !== this.generation) return;
                await this.evaluateQuery(scope, queue.shift(), generation);
            }
        };

        await Promise.all(
            Array.from({ length: Math.min(EVALUATION_CONCURRENCY, queue.length) }, worker),
        );
    }

    async evaluateQuery(scope, query, generation) {
        try {
            const result = await fetchQueryPages({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                query,
            });

            if (hasDuplicateKeys(result.rows)) {
                throw new Error(this.props.t('tools.gsc.duplicate_rows'));
            }
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setEvaluation(query, {
                status: 'done',
                error: null,
                rows: result.rows,
                // A full chunk may have more behind it: the shares are then of the rows retrieved,
                // and the review state says so.
                capped: result.returnedRows >= result.requestedRows,
            });
        } catch (err) {
            console.log('evaluate', query, err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setEvaluation(query, {
                status: 'error',
                error: err?.message ?? this.props.t('global.something_went_wrong'),
                rows: [],
                capped: false,
            });
        }
    }

    /** The rule is read over the evaluations in memory: nothing is fetched when it changes. */
    handleRule(patch) {
        const rule = { ...this.state.rule, ...patch };
        this.setState({ rule });
        this.writeAddress(undefined, rule);
    }

    handleReview(review) {
        this.setState({ review });
        this.writeAddress(undefined, undefined, review);
    }

    /** Opens a query's evidence in block 03 — already fetched, so this costs nothing. */
    handleOpenEvidence(query) {
        this.setState({ evidenceQuery: query }, () => scrollToBlock(this.evidenceRef.current));
    }

    fetchedAt(format = 'YYYY-MM-DD HH:mm') {
        return this.state.fetchedAt ? moment(this.state.fetchedAt).format(format) : null;
    }

    exportName() {
        const scope = this.state.applied;
        return `cannibalization-review-${scope?.siteUrl ?? 'property'}-${
            scope?.current?.startDate ?? ''
        }`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    /** Everything the review currently holds, read under the rule in force. */
    review(scope) {
        const { selected, evaluations, rule, shortlist } = this.state;
        const reads = readSelection(scope, selected, evaluations, rule);
        const candidates = candidateRows(scope, selected, reads);
        const counts = coverageCounts(candidates, shortlist.chunk?.rows.length ?? 0);

        const evaluated = candidates.filter((row) => row.retrievedUrls !== null);
        const evidence =
            evaluated.find((row) => row.key === this.state.evidenceQuery) ??
            evaluated.find((row) => row.state === STATE.OVERLAP) ??
            evaluated[0] ??
            null;

        return { reads, candidates, counts, evidence };
    }

    handleExport() {
        const scope = this.state.applied;
        if (!scope) return;

        const { reads, candidates, counts } = this.review(scope);

        downloadCsv(
            buildReviewCsv({
                t: this.props.t,
                scope,
                rule: this.state.rule,
                candidates,
                reads,
                counts,
                fetchedAt: this.fetchedAt(),
            }),
            `${this.exportName()}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls are left out of the frame; the method block, which states the
     * rule in words, stays in it, so the picture still says what it is a picture of.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-criteria') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName()}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    /** Block 01's method table: each step, stated with what this run actually did. */
    methodRows(scope, counts) {
        const { t } = this.props;
        const { rule, shortlist } = this.state;
        const stop = shortlist.chunk ? t(`tools.gsc.stop.${shortlist.chunk.stopReason}`) : '';

        return [
            {
                id: 'strategy',
                field: t('tools.cannibalization.method.strategy'),
                value: t('tools.cannibalization.method.strategy_value'),
            },
            {
                id: 'shortlist',
                field: t('tools.cannibalization.method.shortlist'),
                value: t('tools.cannibalization.method.shortlist_value', {
                    period: formatPeriod(scope.current),
                    rows: numeral(counts.retrieved).format('0,0'),
                    stop,
                }),
            },
            {
                id: 'selection',
                field: t('tools.cannibalization.method.selection'),
                value: t('tools.cannibalization.method.selection_value', {
                    selected: counts.selected,
                    max: MAX_SELECTED,
                }),
            },
            {
                id: 'evaluate',
                field: t('tools.cannibalization.method.evaluate'),
                value: t('tools.cannibalization.method.evaluate_value', counts),
            },
            {
                id: 'rule',
                field: t('tools.cannibalization.method.rule'),
                value: t('tools.cannibalization.method.rule_value', {
                    impressions: numeral(rule.minImpressions).format('0,0'),
                    urls: rule.minUrls,
                    share: Math.round(rule.minShare * 100),
                }),
            },
            {
                id: 'denominator',
                field: t('tools.cannibalization.method.denominator'),
                value: t('tools.cannibalization.method.denominator_value'),
            },
        ];
    }

    /** Block 04's interpretation and coverage: what this review can and cannot say. */
    interpretationRows(counts) {
        const { t } = this.props;

        return [
            {
                id: 'navigation',
                field: t('tools.cannibalization.coverage.navigation'),
                value: t('tools.cannibalization.coverage.navigation_value', {
                    flagged: counts.flagged,
                }),
            },
            {
                id: 'intent',
                field: t('tools.cannibalization.coverage.intent'),
                value: t('tools.cannibalization.coverage.intent_value'),
            },
            {
                id: 'evaluation',
                field: t('tools.cannibalization.coverage.evaluation'),
                value: t('tools.cannibalization.coverage.evaluation_value', {
                    ...counts,
                    max: MAX_SELECTED,
                }),
                tone: counts.failed ? 'loss' : undefined,
            },
            {
                id: 'candidates',
                field: t('tools.cannibalization.coverage.candidates'),
                value: t('tools.cannibalization.coverage.candidates_value', counts),
            },
            {
                id: 'history',
                field: t('tools.cannibalization.coverage.history'),
                value: t('tools.cannibalization.coverage.history_value'),
            },
            {
                id: 'actions',
                field: t('tools.cannibalization.coverage.actions'),
                value: t('tools.cannibalization.coverage.actions_value'),
            },
            {
                id: 'export',
                field: t('tools.cannibalization.coverage.export'),
                value: t('tools.cannibalization.coverage.export_value'),
            },
        ];
    }

    renderReport(scope) {
        const { t } = this.props;
        const { shortlist, selected, rule, review } = this.state;

        const { reads, candidates, counts, evidence } = this.review(scope);
        const shown = filterByReview(candidates, review);
        const evaluating = candidates.some((row) => row.state === STATE.LOADING);
        const evidenceRead = evidence ? reads[evidence.key] : null;

        const fileName = (title) => `${title} — ${this.exportName()}`;
        const fieldColumns = (field, value) =>
            fieldValueColumns(
                t,
                t(`tools.cannibalization.columns.${field}`),
                t(`tools.cannibalization.columns.${value}`),
            );

        const candidateTable = candidateColumns(t);
        const evidenceTable = evidenceColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
            rule,
        });

        return (
            <>
                <CoverageStrip
                    scope={scope}
                    current={{
                        status: shortlist.status === 'idle' ? 'loading' : shortlist.status,
                        coverage: null,
                        error: shortlist.error,
                    }}
                    baseline={null}
                    fetchedAt={this.state.fetchedAt}
                    statusNote={t('tools.cannibalization.coverage.strip', counts)}
                />

                <EvidenceBlock
                    number="01"
                    title={t('tools.cannibalization.method.title')}
                    description={t('tools.cannibalization.method.description', {
                        max: MAX_SELECTED,
                    })}
                    banner={<RuleControls rule={rule} onChange={this.handleRule} />}
                    columns={fieldColumns('step', 'scope')}
                    rows={this.methodRows(scope, counts)}
                    tableProps={{
                        onlyTable: true,
                        notUseDefaultStyle: true,
                        customContainerStyles: 'gsc-tool-rules-table',
                    }}
                    notes={[
                        t('tools.cannibalization.method.explicit_note'),
                        t('tools.cannibalization.method.budget_note', { max: MAX_SELECTED }),
                    ]}
                >
                    <Shortlist
                        scope={scope}
                        status={shortlist.status}
                        error={shortlist.error}
                        rows={this.shortlistRowsFor(scope)}
                        selected={selected}
                        toEvaluate={this.toEvaluate().length}
                        evaluating={evaluating}
                        stopReason={shortlist.chunk?.stopReason ?? 'empty_page'}
                        loadingMore={shortlist.loadingMore}
                        fileName={fileName(t('tools.cannibalization.shortlist.title'))}
                        onToggle={this.handleToggleQuery}
                        onSelectTop={this.handleSelectTop}
                        onClear={this.handleClearSelection}
                        onEvaluate={this.handleEvaluate}
                        onLoadMore={this.handleLoadMore}
                    />
                </EvidenceBlock>

                <EvidenceBlock
                    number="02"
                    title={t('tools.cannibalization.candidates.title')}
                    description={t('tools.cannibalization.candidates.description', counts)}
                    columns={[
                        ...candidateTable,
                        evidenceActionColumn(t, {
                            activeQuery: evidence?.key,
                            onOpen: this.handleOpenEvidence,
                        }),
                    ]}
                    rows={shown}
                    fileName={fileName(t('tools.cannibalization.candidates.title'))}
                    emptyMessage={
                        selected.length
                            ? t('tools.cannibalization.candidates.empty_review')
                            : t('tools.cannibalization.candidates.empty')
                    }
                    banner={
                        counts.evaluated > 0 && !counts.overlap ? (
                            <div className="gsc-tool-banner">
                                {t('tools.cannibalization.candidates.none_met', counts)}
                            </div>
                        ) : null
                    }
                    tableProps={{
                        customColumnsForExport: candidateTable,
                        additionalBox: () => (
                            <ReviewFilter
                                review={review}
                                counts={{
                                    [REVIEW.ALL]: candidates.length,
                                    [REVIEW.NAVIGATION]: counts.flagged,
                                }}
                                onReview={this.handleReview}
                            />
                        ),
                        rowClassName: (item) => (item.key === evidence?.key ? 'dt-row-picked' : ''),
                        onRowClick: (item) =>
                            item.retrievedUrls !== null && this.handleOpenEvidence(item.key),
                    }}
                    notes={[
                        t('tools.cannibalization.candidates.counts_note'),
                        t('tools.cannibalization.candidates.recalc_note'),
                    ]}
                />

                <div ref={this.evidenceRef}>
                    <EvidenceBlock
                        number="03"
                        title={
                            evidence
                                ? t('tools.cannibalization.evidence.title', { query: evidence.key })
                                : t('tools.cannibalization.evidence.title_idle')
                        }
                        description={
                            evidenceRead
                                ? t('tools.cannibalization.evidence.description', {
                                      total: numeral(evidenceRead.total).format('0,0'),
                                      urls: evidenceRead.pages.length,
                                  })
                                : t('tools.cannibalization.evidence.description_idle')
                        }
                        columns={[...evidenceTable, evidenceLinkColumn(t, scope)]}
                        rows={evidenceRead?.pages ?? []}
                        fileName={fileName(
                            t('tools.cannibalization.evidence.file', {
                                query: evidence?.key ?? '',
                            }),
                        )}
                        emptyMessage={
                            evidence
                                ? t('tools.cannibalization.evidence.empty')
                                : t('tools.cannibalization.evidence.idle')
                        }
                        banner={
                            evidenceRead?.capped ? (
                                <div className="gsc-tool-banner">
                                    {t('tools.cannibalization.evidence.capped')}
                                </div>
                            ) : null
                        }
                        tableProps={{ customColumnsForExport: evidenceTable }}
                        notes={[
                            t('tools.cannibalization.evidence.denominator_note'),
                            t('tools.cannibalization.evidence.position_note'),
                            t('tools.cannibalization.evidence.links_note'),
                        ]}
                    />
                </div>

                <EvidenceBlock
                    number="04"
                    title={t('tools.cannibalization.coverage.title')}
                    description={t('tools.cannibalization.coverage.description')}
                    columns={fieldColumns('check', 'reading')}
                    rows={this.interpretationRows(counts)}
                    fileName={fileName(t('tools.cannibalization.coverage.title'))}
                    actions={
                        <button
                            type="button"
                            title={t('tools.cannibalization.coverage.export_button')}
                            aria-label={t('tools.cannibalization.coverage.export_button')}
                            onClick={this.handleExport}
                            disabled={!selected.length}
                        >
                            <i className="icon-fi_download" />
                        </button>
                    }
                    notes={[
                        t('tools.cannibalization.coverage.no_candidates_note'),
                        t('tools.cannibalization.coverage.exception_note'),
                    ]}
                />

                <p className="gsc-tool-table-note">
                    {t('tools.cannibalization.footnote', { period: formatPeriod(scope.current) })}
                </p>
            </>
        );
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = this.state.shortlist.status === 'done';

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.cannibalization.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.cannibalization.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    maxDays={DETAIL_MAX_DAYS}
                    hideComparison
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                />

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-list-ol" />
                            </span>
                            <h4>{t('tools.cannibalization.idle_title')}</h4>
                            <p>{t('tools.cannibalization.idle', { max: MAX_SELECTED })}</p>
                        </div>
                    </div>
                ) : (
                    this.renderReport(applied)
                )}
            </div>
        );
    }
}

export default withTranslation()(Cannibalization);
